import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, apiError, fmtIDR } from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { ArrowLeft, Plus, Pencil, Trash2, Download, Wallet, TrendingUp, TrendingDown, Landmark, Coins, Boxes, ArrowRightLeft, ImageIcon } from "lucide-react";
import jsPDF from "jspdf";

const finPhotoUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/finance/photo/${id}`;
async function compressImg(file, maxSide = 1200, q = 0.8) {
  try {
    const url = await new Promise((r, j) => { const fr = new FileReader(); fr.onload = () => r(fr.result); fr.onerror = j; fr.readAsDataURL(file); });
    const img = await new Promise((r, j) => { const im = new Image(); im.onload = () => r(im); im.onerror = j; im.src = url; });
    const s = Math.min(1, maxSide / Math.max(img.width, img.height));
    const w = Math.round(img.width * s), h = Math.round(img.height * s);
    const c = document.createElement("canvas"); c.width = w; c.height = h;
    const ctx = c.getContext("2d"); ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, w, h); ctx.drawImage(img, 0, 0, w, h);
    const blob = await new Promise((r) => c.toBlob(r, "image/jpeg", q));
    return blob ? new File([blob], "bukti.jpg", { type: "image/jpeg" }) : file;
  } catch { return file; }
}

const MONTHS_ID = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const TYPE_LABEL = { kas_masuk: "Kas Masuk", kas_keluar: "Kas Keluar", penghapusan_aset: "Penghapusan Aset", transfer: "Transfer Antar Akun" };
const REMOVAL = ["Rusak", "Hilang", "Dijual", "Dihapus"];
const SUBS = [["ringkasan", "Ringkasan"], ["transaksi", "Transaksi"], ["aruskas", "Arus Kas"], ["labarugi", "Laba Rugi Internal"], ["aset", "Daftar Aset"], ["laporan", "Laporan Bulanan"]];
const EMPTY = { date: new Date().toISOString().slice(0, 10), branch_id: "", type: "kas_masuk", category: "", subcategory: "", amount: "", account: "Kas", to_account: "Bank", note: "", ref: "", asset_id: "", asset_name: "", removal_reason: "", removal_status: "Rusak", photo_id: "" };

const monthLabel = (ym) => { const [y, m] = ym.split("-"); return `${MONTHS_ID[Number(m) - 1]} ${y}`; };

function StatCard({ icon: Icon, label, value, tone = "crimson", testid }) {
  return (
    <Card data-testid={testid} className="card-surface rounded-2xl p-4">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><Icon size={15} className={`text-${tone}`} /> {label}</div>
      <div className="mt-1 font-mono-num text-xl font-extrabold text-neutral-900">{fmtIDR(value)}</div>
    </Card>
  );
}

function Row({ label, value, bold, indent }) {
  return (
    <div className={`flex items-center justify-between py-1.5 ${bold ? "border-t border-neutral-200 font-bold text-neutral-900" : "text-neutral-700"}`}>
      <span className={indent ? "pl-4 text-sm" : "text-sm"}>{label}</span>
      <span className="font-mono-num text-sm">{fmtIDR(value)}</span>
    </div>
  );
}

export default function FinanceDashboard({ onHome }) {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [branchSel, setBranchSel] = useState("all");
  const [sub, setSub] = useState("ringkasan");
  const [report, setReport] = useState(null);
  const [txns, setTxns] = useState([]);
  const [assets, setAssets] = useState([]);
  const [meta, setMeta] = useState(null);
  const [typeFilter, setTypeFilter] = useState("all");
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const branches = meta?.branches || [];
  const multiBranch = branches.length > 1;
  const branchParam = branchSel === "all" ? "" : branchSel;
  const branchName = (id) => branches.find((b) => b.id === id)?.name || "-";

  const loadMeta = async () => { try { const { data } = await api.post("/finance/meta"); setMeta(data); } catch (e) { } };
  const loadReport = async () => { try { const { data } = await api.get("/finance/report", { params: { month, branch_id: branchParam } }); setReport(data); } catch (e) { toast.error(apiError(e)); } };
  const loadTxns = async () => { try { const { data } = await api.get("/finance/transactions", { params: { month, branch_id: branchParam } }); setTxns(data); } catch (e) { } };
  const loadAssets = async () => { try { const { data } = await api.get("/finance/assets", { params: { branch_id: branchParam } }); setAssets(data); } catch (e) { } };
  const reloadAll = () => { loadReport(); loadTxns(); loadAssets(); };

  useEffect(() => { loadMeta(); }, []);
  useEffect(() => { if (meta && !multiBranch && branches[0]) setBranchSel(branches[0].id); /* eslint-disable-next-line */ }, [meta]);
  useEffect(() => { loadReport(); loadTxns(); loadAssets(); /* eslint-disable-next-line */ }, [month, branchSel]);

  const catOptions = useMemo(() => {
    if (!meta) return [];
    if (form.type === "kas_masuk") return [...meta.income, ...meta.nonincome_in];
    if (form.type === "kas_keluar") return [...meta.expense, ...meta.asset, ...meta.liability, ...meta.owner];
    return [];
  }, [meta, form.type]);

  const isAssetCat = meta && form.type === "kas_keluar" && meta.asset.includes(form.category);
  const activeAssets = assets.filter((a) => a.status === "Aktif");

  const openAdd = () => { setForm({ ...EMPTY, date: new Date().toISOString().slice(0, 10), branch_id: branchSel !== "all" ? branchSel : (branches[0]?.id || "") }); setEditId(null); setDlg(true); };
  const openEdit = (t) => {
    setForm({ date: t.date, branch_id: t.branch_id || "", type: t.type, category: t.type === "penghapusan_aset" ? "" : t.category, subcategory: t.subcategory || "", amount: String(t.amount || ""), account: t.account || "Kas", to_account: t.to_account || "Bank", note: t.note || "", ref: t.ref || "", asset_id: t.asset_id || "", asset_name: t.asset_name || "", removal_reason: t.removal_reason || "", removal_status: "Rusak", photo_id: t.photo_id || "" });
    setEditId(t.id); setDlg(true);
  };

  const onTypeChange = (v) => setForm((f) => ({ ...f, type: v, category: "", asset_id: "", amount: v === "penghapusan_aset" ? "" : f.amount }));

  const uploadBukti = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("File harus gambar"); return; }
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", await compressImg(f)); const { data } = await api.post("/finance/photo", fd); setForm((s) => ({ ...s, photo_id: data.id })); }
    catch (err) { toast.error(apiError(err)); } finally { setUploading(false); if (e.target) e.target.value = ""; }
  };

  const save = async () => {
    const body = { ...form, amount: Number(form.amount || 0) };
    if (form.type !== "penghapusan_aset" && !form.branch_id) { toast.error("Pilih cabang"); return; }
    if (form.type !== "penghapusan_aset" && body.amount <= 0) { toast.error("Nominal harus lebih dari 0"); return; }
    if (form.type === "penghapusan_aset" && !form.asset_id) { toast.error("Pilih aset yang akan dihapus"); return; }
    setSaving(true);
    try {
      if (editId) await api.put(`/finance/transactions/${editId}`, body); else await api.post("/finance/transactions", body);
      toast.success(editId ? "Transaksi diperbarui" : "Transaksi ditambahkan");
      setDlg(false); reloadAll();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const del = async (t) => {
    if (!window.confirm("Apakah Anda yakin ingin menghapus transaksi ini?")) return;
    try { await api.delete(`/finance/transactions/${t.id}`); toast.success("Transaksi dihapus"); reloadAll(); } catch (e) { toast.error(apiError(e)); }
  };

  const shownTxns = txns.filter((t) => typeFilter === "all" || t.type === typeFilter);
  const dailyData = report ? report.daily.income.map((v, i) => ({ hari: String(i + 1), Pendapatan: v, Pengeluaran: report.daily.expense[i] })) : [];

  const exportPDF = () => {
    if (!report) return;
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();
    const M = 40; let y = 0;
    const rupiah = (n) => "Rp " + (Number(n) || 0).toLocaleString("id-ID");
    let page = 1;
    const footer = () => {
      doc.setFontSize(8); doc.setTextColor(120);
      doc.text(`Dicetak: ${new Date().toLocaleString("id-ID")}`, M, H - 20);
      doc.text("DA LE TANG HuoGuo", W / 2, H - 20, { align: "center" });
      doc.text(`Halaman ${page}`, W - M, H - 20, { align: "right" });
    };
    const header = () => {
      doc.setFillColor(220, 38, 38); doc.rect(0, 0, W, 70, "F");
      doc.setTextColor(255); doc.setFont("helvetica", "bold"); doc.setFontSize(16);
      doc.text("DA LE TANG HuoGuo", M, 30);
      doc.setFontSize(11); doc.setFont("helvetica", "normal");
      doc.text("LAPORAN KEUANGAN BULANAN", M, 48);
      doc.setFontSize(10); doc.text(`Periode: ${monthLabel(report.month)}`, M, 62);
      doc.text(`Cabang: ${branchSel === "all" ? "Semua Cabang" : branchName(branchSel)}`, W - M, 62, { align: "right" });
      doc.setTextColor(0); y = 92;
    };
    const newPageIfNeeded = (need = 20) => { if (y + need > H - 40) { footer(); doc.addPage(); page += 1; header(); } };
    const section = (title) => { newPageIfNeeded(30); doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(220, 38, 38); doc.text(title, M, y); doc.setTextColor(0); y += 16; };
    const line = (label, val, opts = {}) => {
      newPageIfNeeded(16); doc.setFont("helvetica", opts.bold ? "bold" : "normal"); doc.setFontSize(9.5);
      doc.text(String(label), M + (opts.indent ? 14 : 0), y);
      doc.text(rupiah(val), W - M, y, { align: "right" }); y += 14;
    };
    const textline = (label) => { newPageIfNeeded(16); doc.setFont("helvetica", "normal"); doc.setFontSize(9.5); doc.text(String(label), M, y); y += 14; };

    header();
    const s = report.summary;
    section("1. RINGKASAN KEUANGAN");
    line("Total Pendapatan", s.omzet); line("Total Beban", s.total_beban); line("Laba Bersih Internal", s.laba, { bold: true });
    line("Saldo Awal", report.cashflow.saldo_awal); line("Total Kas Masuk", report.cashflow.total_masuk); line("Total Kas Keluar", report.cashflow.total_keluar); line("Saldo Akhir", report.cashflow.saldo_akhir, { bold: true });
    line("Total Aset Aktif", report.balance.aset_aktif); line("Total Hutang", report.balance.total_kewajiban); line("Total Modal", report.balance.total_modal); y += 8;

    section("2. ARUS KAS");
    line("Saldo Awal", report.cashflow.saldo_awal, { bold: true });
    textline("Kas Masuk:");
    Object.entries(report.cashflow.masuk).forEach(([k, v]) => line(k, v, { indent: true }));
    textline("Kas Keluar:");
    Object.entries(report.cashflow.keluar).forEach(([k, v]) => line(k, v, { indent: true }));
    line("Saldo Akhir", report.cashflow.saldo_akhir, { bold: true }); y += 8;

    section("3. LABA RUGI INTERNAL");
    textline("Pendapatan:");
    Object.entries(report.pnl.pendapatan).forEach(([k, v]) => line(k, v, { indent: true }));
    line("Total Pendapatan", report.pnl.total_pendapatan, { bold: true });
    textline("Beban:");
    Object.entries(report.pnl.beban).forEach(([k, v]) => line(k, v, { indent: true }));
    line("Total Beban", report.pnl.total_beban, { bold: true });
    line("Laba Bersih Internal", report.pnl.laba, { bold: true }); y += 8;

    section("4. NERACA SEDERHANA");
    const b = report.balance;
    line("Kas", b.kas, { indent: true }); line("Bank", b.bank, { indent: true }); line("Saldo QRIS/EDC belum diterima", b.receivable, { indent: true }); line("Aset Aktif", b.aset_aktif, { indent: true });
    line("TOTAL ASET", b.total_aset, { bold: true });
    line("Hutang Supplier", b.hutang_supplier, { indent: true }); line("Sisa Pinjaman", b.pinjaman_sisa, { indent: true });
    line("Modal Pemilik", b.modal_pemilik, { indent: true }); line("Penerimaan Lainnya", b.penerimaan_lain, { indent: true }); line("Laba Bersih Internal", b.laba, { indent: true }); line("Prive Pemilik", -b.prive, { indent: true });
    line("TOTAL KEWAJIBAN + MODAL", b.total_pasiva, { bold: true });
    textline(b.balanced ? "Status: NERACA SEIMBANG" : "Status: PERLU DIPERIKSA"); y += 8;

    section("5. DAFTAR ASET AKTIF");
    if (!report.assets_active.length) textline("- Belum ada aset aktif.");
    report.assets_active.forEach((a) => line(`${a.name} (${a.category}) · ${a.purchase_date}`, a.purchase_amount, { indent: true }));
    if (report.assets_changed.length) {
      y += 4; textline("Aset Rusak/Dihapus bulan ini:");
      report.assets_changed.forEach((a) => line(`${a.name} · ${a.status} · ${a.removal_date}`, a.purchase_amount, { indent: true }));
    }
    footer();
    doc.save(`Laporan-Keuangan-${report.month}.pdf`);
  };

  return (
    <div className="min-h-screen bg-hotpot-grid pb-16" data-testid="finance-dashboard">
      <TopBar />
      <div className="mx-auto max-w-[1200px] px-4 pt-6 sm:px-6">
        {onHome && <button data-testid="finance-home-btn" onClick={onHome} className="mb-3 inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-crimson"><ArrowLeft size={16} /> Menu Utama</button>}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-neutral-900 sm:text-3xl">Keuangan</h1>
          <div className="flex flex-wrap items-center gap-2">
            {branches.length > 0 && (
              <Select value={branchSel} onValueChange={setBranchSel}>
                <SelectTrigger data-testid="finance-branch-filter" className="w-48 bg-white border-neutral-200"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {multiBranch && <SelectItem value="all">Semua Cabang</SelectItem>}
                  {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Input data-testid="finance-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40 bg-white border-neutral-200" />
            <Button data-testid="finance-add-btn" onClick={openAdd} className="bg-crimson font-bold hover:bg-red-500"><Plus size={16} className="mr-1" /> Tambah Transaksi</Button>
          </div>
        </div>

        {/* Submenu */}
        <div className="mt-4 flex flex-wrap gap-2">
          {SUBS.map(([k, label]) => (
            <button key={k} data-testid={`finance-sub-${k}`} onClick={() => setSub(k)}
              className={`rounded-full px-4 py-1.5 text-sm font-bold transition-colors ${sub === k ? "bg-crimson text-white" : "bg-crimson/10 text-crimson hover:bg-crimson/20"}`}>{label}</button>
          ))}
        </div>

        {report && sub === "ringkasan" && (
          <div className="mt-5 space-y-5" data-testid="finance-ringkasan">
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <StatCard testid="stat-omzet" icon={TrendingUp} label={`Omzet ${MONTHS_ID[Number(month.slice(5, 7)) - 1]}`} value={report.summary.omzet} />
              <StatCard testid="stat-beban" icon={TrendingDown} label="Total Beban" value={report.summary.total_beban} />
              <StatCard testid="stat-laba" icon={Wallet} label="Laba Bersih Internal" value={report.summary.laba} />
              <StatCard testid="stat-kas" icon={Coins} label="Saldo Kas" value={report.summary.saldo_kas} />
              <StatCard testid="stat-bank" icon={Landmark} label="Saldo Bank" value={report.summary.saldo_bank} />
              <StatCard testid="stat-aset" icon={Boxes} label="Total Aset Aktif" value={report.summary.total_aset_aktif} />
              <StatCard testid="stat-hutang" icon={ArrowRightLeft} label="Total Hutang" value={report.summary.total_hutang} />
            </div>
            <Card className="card-surface rounded-2xl p-5">
              <div className="mb-3 font-display text-base font-bold text-neutral-900">Pendapatan & Pengeluaran per Hari — {monthLabel(month)}</div>
              <div style={{ width: "100%", height: 300 }}>
                <ResponsiveContainer>
                  <BarChart data={dailyData} margin={{ top: 6, right: 8, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="hari" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000000 ? `${v / 1000000}jt` : v >= 1000 ? `${v / 1000}rb` : v} />
                    <Tooltip formatter={(v) => fmtIDR(v)} />
                    <Legend />
                    <Bar dataKey="Pendapatan" fill="#16a34a" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="Pengeluaran" fill="#dc2626" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>
        )}

        {sub === "transaksi" && (
          <div className="mt-5 space-y-3" data-testid="finance-transaksi">
            <div className="flex items-center gap-2">
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger data-testid="finance-type-filter" className="w-56 bg-white border-neutral-200"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Jenis</SelectItem>
                  {Object.entries(TYPE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground">{shownTxns.length} transaksi · {monthLabel(month)}</span>
            </div>
            <Card className="card-surface overflow-hidden rounded-2xl">
              <div className="divide-y divide-neutral-100" data-testid="finance-txn-list">
                {shownTxns.length === 0 && <div className="p-5 text-sm text-muted-foreground">Belum ada transaksi bulan ini.</div>}
                {shownTxns.map((t) => {
                  const income = t.type === "kas_masuk";
                  const neutral = t.type === "transfer" || t.type === "penghapusan_aset";
                  return (
                    <div key={t.id} data-testid={`finance-txn-${t.id}`} className="flex flex-wrap items-center gap-3 px-4 py-3">
                      <div className="w-24 text-xs text-muted-foreground">{t.date}</div>
                      {t.photo_id ? (
                        <a href={finPhotoUrl(t.photo_id)} target="_blank" rel="noreferrer" data-testid={`finance-bukti-${t.id}`} className="shrink-0"><img src={finPhotoUrl(t.photo_id)} alt="bukti" className="h-10 w-10 rounded-md border border-neutral-200 object-cover" /></a>
                      ) : null}
                      <div className="min-w-[180px] flex-1">
                        <div className="font-semibold text-neutral-900">{t.type === "transfer" ? `${t.account} → ${t.to_account}` : (t.category || TYPE_LABEL[t.type])}</div>
                        <div className="text-xs text-muted-foreground">{TYPE_LABEL[t.type]}{t.account && t.type !== "transfer" ? ` · ${t.account}` : ""}{multiBranch ? ` · ${branchName(t.branch_id)}` : ""}{t.note ? ` · ${t.note}` : ""}</div>
                      </div>
                      <div className={`w-32 text-right font-mono-num font-bold ${income ? "text-emerald-600" : neutral ? "text-neutral-500" : "text-crimson"}`}>{income ? "+" : neutral ? "" : "-"}{fmtIDR(t.amount)}</div>
                      <div className="flex items-center gap-1">
                        <Button data-testid={`finance-edit-${t.id}`} onClick={() => openEdit(t)} variant="ghost" size="icon" className="text-gold hover:bg-gold/10"><Pencil size={15} /></Button>
                        <Button data-testid={`finance-del-${t.id}`} onClick={() => del(t)} variant="ghost" size="icon" className="text-muted-foreground hover:text-crimson"><Trash2 size={15} /></Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>
        )}

        {report && sub === "aruskas" && (
          <Card className="card-surface mt-5 rounded-2xl p-5" data-testid="finance-aruskas">
            <Row label="Saldo Awal" value={report.cashflow.saldo_awal} bold />
            <div className="mt-2 text-xs font-bold uppercase tracking-wider text-emerald-600">Kas Masuk</div>
            {Object.entries(report.cashflow.masuk).map(([k, v]) => <Row key={k} label={k} value={v} indent />)}
            <Row label="Total Kas Masuk" value={report.cashflow.total_masuk} bold />
            <div className="mt-2 text-xs font-bold uppercase tracking-wider text-crimson">Kas Keluar</div>
            {Object.entries(report.cashflow.keluar).map(([k, v]) => <Row key={k} label={k} value={v} indent />)}
            <Row label="Total Kas Keluar" value={report.cashflow.total_keluar} bold />
            <div className="mt-3 rounded-xl bg-crimson/5 px-4 py-2"><Row label="SALDO AKHIR" value={report.cashflow.saldo_akhir} bold /></div>
          </Card>
        )}

        {report && sub === "labarugi" && (
          <Card className="card-surface mt-5 rounded-2xl p-5" data-testid="finance-labarugi">
            <div className="text-xs font-bold uppercase tracking-wider text-emerald-600">Pendapatan</div>
            {Object.entries(report.pnl.pendapatan).map(([k, v]) => <Row key={k} label={k} value={v} indent />)}
            <Row label="Total Pendapatan" value={report.pnl.total_pendapatan} bold />
            <div className="mt-3 text-xs font-bold uppercase tracking-wider text-crimson">Beban</div>
            {Object.entries(report.pnl.beban).map(([k, v]) => <Row key={k} label={k} value={v} indent />)}
            <Row label="Total Beban" value={report.pnl.total_beban} bold />
            <div className="mt-3 rounded-xl bg-crimson/5 px-4 py-2"><Row label="LABA BERSIH INTERNAL" value={report.pnl.laba} bold /></div>
          </Card>
        )}

        {sub === "aset" && (
          <Card className="card-surface mt-5 overflow-x-auto rounded-2xl p-2" data-testid="finance-aset">
            <table className="w-full min-w-[820px] text-sm">
              <thead>
                <tr className="border-b border-neutral-200 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="p-3">Nama Aset</th><th className="p-3">Kategori</th><th className="p-3">Tgl Beli</th><th className="p-3 text-right">Harga Beli</th><th className="p-3">Akun</th><th className="p-3">Status</th><th className="p-3">Tgl Hapus</th><th className="p-3">Alasan</th>
                </tr>
              </thead>
              <tbody>
                {assets.length === 0 && <tr><td colSpan={8} className="p-5 text-muted-foreground">Belum ada aset. Tambahkan lewat transaksi Kas Keluar kategori aset.</td></tr>}
                {assets.map((a) => (
                  <tr key={a.id} data-testid={`asset-row-${a.id}`} className="border-b border-neutral-100">
                    <td className="p-3 font-semibold text-neutral-900">{a.name}{multiBranch ? <span className="ml-1 rounded-full bg-gold/15 px-2 py-0.5 text-[10px] font-bold text-gold">{branchName(a.branch_id)}</span> : null}</td>
                    <td className="p-3 text-muted-foreground">{a.category}</td>
                    <td className="p-3">{a.purchase_date}</td>
                    <td className="p-3 text-right font-mono-num">{fmtIDR(a.purchase_amount)}</td>
                    <td className="p-3">{a.account}</td>
                    <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-xs font-bold ${a.status === "Aktif" ? "bg-emerald-500/15 text-emerald-600" : "bg-neutral-200 text-neutral-600"}`}>{a.status}</span></td>
                    <td className="p-3 text-muted-foreground">{a.removal_date || "-"}</td>
                    <td className="p-3 text-muted-foreground">{a.removal_reason || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}

        {report && sub === "laporan" && (
          <div className="mt-5 space-y-4" data-testid="finance-laporan">
            <div className="flex items-center justify-between">
              <div className="font-display text-lg font-bold text-neutral-900">Laporan Bulanan — {monthLabel(month)}</div>
              <Button data-testid="finance-pdf-btn" onClick={exportPDF} className="bg-crimson font-bold hover:bg-red-500"><Download size={16} className="mr-1" /> Cetak PDF</Button>
            </div>
            <div className="grid gap-4 lg:grid-cols-2">
              <Card className="card-surface rounded-2xl p-5">
                <div className="mb-2 font-display font-bold text-neutral-900">Ringkasan Keuangan</div>
                <Row label="Total Pendapatan" value={report.summary.omzet} />
                <Row label="Total Beban" value={report.summary.total_beban} />
                <Row label="Laba Bersih Internal" value={report.summary.laba} bold />
                <Row label="Saldo Awal" value={report.cashflow.saldo_awal} />
                <Row label="Total Kas Masuk" value={report.cashflow.total_masuk} />
                <Row label="Total Kas Keluar" value={report.cashflow.total_keluar} />
                <Row label="Saldo Akhir" value={report.cashflow.saldo_akhir} bold />
              </Card>
              <Card className="card-surface rounded-2xl p-5">
                <div className="mb-2 font-display font-bold text-neutral-900">Neraca Sederhana</div>
                <div className="text-xs font-bold uppercase tracking-wider text-emerald-600">Aset</div>
                <Row label="Kas" value={report.balance.kas} indent />
                <Row label="Bank" value={report.balance.bank} indent />
                <Row label="Saldo QRIS/EDC belum diterima" value={report.balance.receivable} indent />
                <Row label="Aset Aktif" value={report.balance.aset_aktif} indent />
                <Row label="TOTAL ASET" value={report.balance.total_aset} bold />
                <div className="mt-2 text-xs font-bold uppercase tracking-wider text-crimson">Kewajiban & Modal</div>
                <Row label="Hutang Supplier" value={report.balance.hutang_supplier} indent />
                <Row label="Sisa Pinjaman" value={report.balance.pinjaman_sisa} indent />
                <Row label="Modal Pemilik" value={report.balance.modal_pemilik} indent />
                <Row label="Penerimaan Lainnya" value={report.balance.penerimaan_lain} indent />
                <Row label="Laba Bersih Internal" value={report.balance.laba} indent />
                <Row label="Prive Pemilik" value={-report.balance.prive} indent />
                <Row label="TOTAL KEWAJIBAN + MODAL" value={report.balance.total_pasiva} bold />
                <div className={`mt-3 rounded-xl px-4 py-2 text-center text-sm font-bold ${report.balance.balanced ? "bg-emerald-500/15 text-emerald-700" : "bg-amber-500/15 text-amber-700"}`} data-testid="finance-balance-status">
                  {report.balance.balanced ? "NERACA SEIMBANG" : "PERLU DIPERIKSA"}
                </div>
              </Card>
            </div>
          </div>
        )}
      </div>

      {/* Add / Edit transaction */}
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="glass max-h-[90vh] overflow-y-auto border-neutral-200 text-neutral-900">
          <DialogHeader><DialogTitle className="font-display">{editId ? "Edit" : "Tambah"} Transaksi</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Form untuk menambah atau mengubah transaksi keuangan restoran.</DialogDescription>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="mb-1 block text-xs font-semibold text-muted-foreground">Tanggal</label>
                <Input data-testid="fin-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="bg-neutral-100 border-neutral-200" /></div>
              <div><label className="mb-1 block text-xs font-semibold text-muted-foreground">Jenis Transaksi</label>
                <Select value={form.type} onValueChange={onTypeChange}>
                  <SelectTrigger data-testid="fin-type" className="bg-neutral-100 border-neutral-200"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(TYPE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>

            <div><label className="mb-1 block text-xs font-semibold text-muted-foreground">Cabang</label>
              <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })} disabled={form.type === "penghapusan_aset"}>
                <SelectTrigger data-testid="fin-branch" className="bg-neutral-100 border-neutral-200"><SelectValue placeholder="Pilih cabang" /></SelectTrigger>
                <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
              {form.type === "penghapusan_aset" && <p className="mt-1 text-[11px] text-muted-foreground">Cabang mengikuti aset yang dipilih.</p>}
            </div>

            {(form.type === "kas_masuk" || form.type === "kas_keluar") && (
              <div><label className="mb-1 block text-xs font-semibold text-muted-foreground">Kategori</label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="fin-category" className="bg-neutral-100 border-neutral-200"><SelectValue placeholder="Pilih kategori" /></SelectTrigger>
                  <SelectContent className="max-h-72">{catOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select></div>
            )}

            {isAssetCat && (
              <div><label className="mb-1 block text-xs font-semibold text-muted-foreground">Nama Aset</label>
                <Input data-testid="fin-asset-name" placeholder="mis. Freezer 200L" value={form.asset_name} onChange={(e) => setForm({ ...form, asset_name: e.target.value })} className="bg-neutral-100 border-neutral-200" />
                <p className="mt-1 text-[11px] text-muted-foreground">Aset akan tercatat di Daftar Aset (tidak dihitung sebagai beban).</p></div>
            )}

            {form.type === "penghapusan_aset" && (
              <>
                <div><label className="mb-1 block text-xs font-semibold text-muted-foreground">Pilih Aset (Aktif)</label>
                  <Select value={form.asset_id} onValueChange={(v) => setForm({ ...form, asset_id: v })}>
                    <SelectTrigger data-testid="fin-asset-select" className="bg-neutral-100 border-neutral-200"><SelectValue placeholder="Pilih aset" /></SelectTrigger>
                    <SelectContent>{activeAssets.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {fmtIDR(a.purchase_amount)}</SelectItem>)}{activeAssets.length === 0 && <div className="px-2 py-1.5 text-xs text-muted-foreground">Tidak ada aset aktif</div>}</SelectContent>
                  </Select></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="mb-1 block text-xs font-semibold text-muted-foreground">Status</label>
                    <Select value={form.removal_status} onValueChange={(v) => setForm({ ...form, removal_status: v })}>
                      <SelectTrigger data-testid="fin-removal-status" className="bg-neutral-100 border-neutral-200"><SelectValue /></SelectTrigger>
                      <SelectContent>{REMOVAL.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div><label className="mb-1 block text-xs font-semibold text-muted-foreground">Alasan</label>
                    <Input data-testid="fin-removal-reason" value={form.removal_reason} onChange={(e) => setForm({ ...form, removal_reason: e.target.value })} className="bg-neutral-100 border-neutral-200" /></div>
                </div>
                <p className="text-[11px] text-muted-foreground">Nilai kerugian otomatis = harga beli aset. Tidak ada kas keluar.</p>
              </>
            )}

            {form.type !== "penghapusan_aset" && (
              <div><label className="mb-1 block text-xs font-semibold text-muted-foreground">Nominal (Rp)</label>
                <Input data-testid="fin-amount" type="number" min="0" placeholder="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="bg-neutral-100 border-neutral-200 font-mono-num" /></div>
            )}

            {form.type === "transfer" ? (
              <div className="grid grid-cols-2 gap-3">
                <div><label className="mb-1 block text-xs font-semibold text-muted-foreground">Dari Akun</label>
                  <Select value={form.account} onValueChange={(v) => setForm({ ...form, account: v })}>
                    <SelectTrigger data-testid="fin-account" className="bg-neutral-100 border-neutral-200"><SelectValue /></SelectTrigger>
                    <SelectContent>{(meta?.accounts || []).map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><label className="mb-1 block text-xs font-semibold text-muted-foreground">Ke Akun</label>
                  <Select value={form.to_account} onValueChange={(v) => setForm({ ...form, to_account: v })}>
                    <SelectTrigger data-testid="fin-to-account" className="bg-neutral-100 border-neutral-200"><SelectValue /></SelectTrigger>
                    <SelectContent>{(meta?.accounts || []).map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                  </Select></div>
              </div>
            ) : form.type !== "penghapusan_aset" && (
              <div><label className="mb-1 block text-xs font-semibold text-muted-foreground">Akun Pembayaran</label>
                <Select value={form.account} onValueChange={(v) => setForm({ ...form, account: v })}>
                  <SelectTrigger data-testid="fin-account" className="bg-neutral-100 border-neutral-200"><SelectValue /></SelectTrigger>
                  <SelectContent>{(meta?.accounts || []).map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                </Select></div>
            )}

            <div><label className="mb-1 block text-xs font-semibold text-muted-foreground">Keterangan</label>
              <Textarea data-testid="fin-note" rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="bg-neutral-100 border-neutral-200" /></div>
            <div><label className="mb-1 block text-xs font-semibold text-muted-foreground">Referensi / No. Transaksi (opsional)</label>
              <Input data-testid="fin-ref" value={form.ref} onChange={(e) => setForm({ ...form, ref: e.target.value })} className="bg-neutral-100 border-neutral-200" /></div>

            {(form.type === "kas_masuk" || form.type === "kas_keluar") && (
              <div>
                <label className="mb-1 block text-xs font-semibold text-muted-foreground">Bukti {form.type === "kas_masuk" ? "Pemasukan" : "Pembelian / Bon"} (opsional)</label>
                <div className="flex items-center gap-3">
                  {form.photo_id ? (
                    <a href={finPhotoUrl(form.photo_id)} target="_blank" rel="noreferrer"><img src={finPhotoUrl(form.photo_id)} alt="bukti" className="h-16 w-16 rounded-lg border border-neutral-200 object-cover" /></a>
                  ) : <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-neutral-100 text-neutral-300"><ImageIcon size={22} /></div>}
                  <label className="cursor-pointer rounded-lg border border-dashed border-neutral-300 px-3 py-2 text-sm font-semibold text-crimson hover:bg-crimson/5">
                    {uploading ? "Mengunggah…" : form.photo_id ? "Ganti Foto" : "Unggah Foto"}
                    <input data-testid="fin-photo" type="file" accept="image/*" className="hidden" onChange={uploadBukti} />
                  </label>
                  {form.photo_id && <button data-testid="fin-photo-remove" onClick={() => setForm({ ...form, photo_id: "" })} className="text-xs text-muted-foreground hover:text-crimson">Hapus</button>}
                </div>
              </div>
            )}

            <Button data-testid="fin-save-btn" onClick={save} disabled={saving} className="w-full bg-crimson font-bold hover:bg-red-500">{saving ? "Menyimpan…" : "Simpan Transaksi"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
